import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import CustomSwitch from '../../../../common/formfields/CustomSwitch';
import apiServices from '../../../../../services/apiService';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';

const UnitNumberForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const isEdit = !!location.state?.id;
    const editId = location.state?.id;
    const editRecord = location.state?.record; // If passed from table

    const [sites, setSites] = useState([]);
    const [wings, setWings] = useState([]);
    const [floorsData, setFloorsData] = useState([]);
    const [unitTypes, setUnitTypes] = useState([]);

    const initialFormState = {
        unitNumberId: 0,
        siteId: null,
        wingId: null,
        floorId: null,
        unitNumbers: ''
    };

    const [formData, setFormData] = useState(initialFormState);

    const fetchDropdowns = async () => {
        setIsFetching(true);
        try {
            const [siteRes, wingRes, floorRes, unitRes] = await Promise.all([
                apiServices({ method: 'GET', url: '/Residential/ResSiteDetailsMaster/getAllSiteDetails' }),
                apiServices({ method: 'GET', url: '/Residential/ResWingMaster/GetAllWing' }),
                apiServices({ method: 'GET', url: '/Residential/ResFloorMaster/GetAllFloor' }),
                apiServices({ method: 'GET', url: '/Residential/ResUnitMaster/GetAllUnitMaster' })
            ]);

            if (siteRes.success) {
                setSites(siteRes.data?.Data?.map(s => ({ label: s.SiteName, value: s.Id })) || []);
            }
            if (wingRes.success) {
                setWings(wingRes.data?.WingMasterData?.map(w => ({ label: w.WingName, value: w.Id })) || []);
            }
            if (floorRes.success) {
                setFloorsData(floorRes.data?.FloorMasterData?.map(f => ({
                    label: f.FloorName,
                    value: f.Id,
                    wingId: f.WingId || f.wingId
                })) || []);
            }
            if (unitRes.success) {
                let uData = unitRes.data?.data || unitRes.data?.UnitMasterData || [];
                setUnitTypes(uData.map(u => ({ label: u.UnitName || u.unitName, value: u.Id || u.UnitId })));
            }

            if (isEdit && editRecord) {
                setFormData({
                    unitNumberId: editRecord.UnitNumberId || editId,
                    siteId: editRecord.SiteId,
                    wingId: editRecord.WingId,
                    floorId: editRecord.FloorId,
                    unitNumbers: editRecord.UnitNumber
                });
            }
        } catch (error) {
            console.error("Error fetching dropdowns:", error);
            toast.error("Failed to load necessary data.");
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        fetchDropdowns();
    }, [isEdit]);

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const next = { ...prev, [key]: option ? option.value : null };
            if (key === 'wingId') next.floorId = null;
            return next;
        });
    };

    const handleSubmit = async () => {
        if (!formData.siteId) return toast.warning("Site is required");
        if (!formData.wingId) return toast.warning("Wing is required");
        if (!formData.floorId) return toast.warning("Floor is required");
        if (!formData.unitNumbers.trim()) return toast.warning("Unit number(s) must be provided");

        const payload = {
            unitNumberId: isEdit ? formData.unitNumberId : 0,
            siteId: formData.siteId,
            wingId: formData.wingId,
            floorId: formData.floorId,
            unitId: 0,
            floors: [{
                floorId: formData.floorId,
                unitNumbers: formData.unitNumbers.split(',').map(s => s.trim()).filter(s => s)
            }],
            status: "Available",
            createdBy: "Admin",
            modifiedBy: "Admin",
            isActiveId: 1
        };


        setLoading(true);
        try {
            let response;
            if (isEdit) {
                response = await apiServices({
                    method: 'PUT',
                    url: `/Residential/ResUnitNumberMaster/UpdateExistingUnitNumber/${formData.unitNumberId}`,
                    data: payload
                });
            } else {
                response = await apiServices({
                    method: 'POST',
                    url: `/Residential/ResUnitNumberMaster/AddNewUnitNumber`,
                    data: payload
                });
            }

            if (response.IsSuccess || response.success) {
                toast.success(`Unit numbers ${isEdit ? 'updated' : 'added'} successfully`);
                navigate('/zentres/site-management/unit', { state: { tab: 'master' } });
            } else {
                toast.error(response.Message || "Failed to save unit numbers");
            }
        } catch (error) {
            console.error("Save error:", error);
            toast.error("An error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading={isEdit ? "Update Unit Numbers" : "Create Unit Numbers"}
                subHeading={isEdit ? "Update physical unit configuration" : "Register physical unit numbers for your floors"}
                Navbar={true}
            />

            {isFetching ? (
                <div className="flex items-center justify-center h-40">
                    <span className="text-gray-500 font-medium text-sm">Loading form data...</span>
                </div>
            ) : (
                <div className="flex-1 flex flex-col">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-4 mb-8">
                        <CustomSelect
                            label={<>SITE NAME <span className="text-red-500">*</span></>}
                            options={sites}
                            value={sites.find(s => s.value === formData.siteId) || null}
                            onChange={(opt) => handleSelectChange('siteId', opt)}
                            placeholder="Select Site"
                        />

                        <CustomSelect
                            label={<>WING <span className="text-red-500">*</span></>}
                            options={wings}
                            value={wings.find(w => w.value === formData.wingId) || null}
                            onChange={(opt) => handleSelectChange('wingId', opt)}
                            placeholder="Select Wing"
                        />

                        <CustomSelect
                            label={<>FLOOR <span className="text-red-500">*</span></>}
                            options={floorsData.filter(f => !formData.wingId || f.wingId == formData.wingId)}
                            value={floorsData.find(f => f.value === formData.floorId) || null}
                            onChange={(opt) => handleSelectChange('floorId', opt)}
                            placeholder="Select Floor"
                            isDisabled={isEdit}
                        />

                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                {isEdit ? "UNIT NUMBER" : "UNIT NUMBERS (COMMA SEPARATED)"} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.unitNumbers}
                                onChange={(e) => setFormData(prev => ({ ...prev, unitNumbers: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                                placeholder={isEdit ? "e.g. 814" : "e.g. 801, 802, 803"}
                            />
                        </div>
                    </div>

                    <div className="flex justify-between mt-auto border-t dark:border-bordercolor pt-4">
                        <button
                            onClick={() => navigate('/zentres/site-management/unit', { state: { tab: 'master' } })}
                            className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                        >
                            <FontAwesomeIcon icon={faAngleLeft} />
                            Cancel
                        </button>

                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                            style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }}
                        >
                            {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                            <FontAwesomeIcon icon={faAngleRight} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UnitNumberForm;
